import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";

const CustomerAuthContext = createContext(null);

function customerHeaders() {
  const token = localStorage.getItem("tw_customer_token");
  return token ? { "X-Customer-Authorization": `Bearer ${token}` } : {};
}

export function CustomerAuthProvider({ children }) {
  const [customer, setCustomer] = useState(undefined);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/customer-auth/me", { headers: customerHeaders() });
      setCustomer(data);
      return data;
    } catch {
      localStorage.removeItem("tw_customer_token");
      setCustomer(null);
      return null;
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const authenticate = useCallback(async (endpoint, payload) => {
    try {
      const { data } = await api.post(endpoint, payload);
      if (data.access_token) localStorage.setItem("tw_customer_token", data.access_token);
      setCustomer(data.user);
      return { ok: true, user: data.user };
    } catch (error) {
      return { ok: false, error: formatApiError(error.response?.data?.detail) || error.message };
    }
  }, []);

  const register = useCallback((payload) => authenticate("/customer-auth/register", payload), [authenticate]);
  const login = useCallback((email, password) => authenticate("/customer-auth/login", { email, password }), [authenticate]);
  const logout = useCallback(async () => {
    try { await api.post("/customer-auth/logout", {}, { headers: customerHeaders() }); } catch {}
    localStorage.removeItem("tw_customer_token");
    setCustomer(null);
  }, []);
  const startCheckout = useCallback(async (plan = "monthly") => {
    const { data } = await api.post("/subscriptions/checkout", { plan }, { headers: customerHeaders() });
    return data;
  }, []);
  const verifyPayment = useCallback(async (reference) => {
    const { data } = await api.get(`/subscriptions/payments/${reference}`, { headers: customerHeaders() });
    await refresh();
    return data;
  }, [refresh]);

  const value = useMemo(() => ({ customer, register, login, logout, refresh, startCheckout, verifyPayment }), [customer, register, login, logout, refresh, startCheckout, verifyPayment]);
  return <CustomerAuthContext.Provider value={value}>{children}</CustomerAuthContext.Provider>;
}

export function useCustomerAuth() {
  return useContext(CustomerAuthContext);
}

export { customerHeaders };
